import Button from '@mui/material/Button/index.js'
import Card from '@mui/material/Card/index.js'
import CardActions from '@mui/material/CardActions/index.js'
import CardHeader from '@mui/material/CardHeader/index.js'
import { useContext } from 'react'

import { itemsMap } from '../../data/maps.js'
import { items, wines } from '../../img/index.js'
import { cellarService } from '../../services/cellar.js'
import { wineService } from '../../services/wine.js'
import { getKegDisplayName } from '../../utils/getKegDisplayName.js'
import { getKegSpoilageRate } from '../../utils/getKegSpoilageRate.js'
import { getKegValue } from '../../utils/getKegValue.js'
import { getSalePriceMultiplier } from '../../utils/getSalePriceMultiplier.js'
import { moneyString } from '../../utils/moneyString.js'
import AnimatedNumber from '../AnimatedNumber/index.js'
import FarmhandContext from '../Farmhand/Farmhand.context.js'

export function Keg({ keg }: { keg: farmhand.keg }) {
  const {
    handlers: { handleSellKegClick, handleThrowAwayKegClick },
    gameState: { completedAchievements },
  } = useContext(FarmhandContext)

  const item = itemsMap[keg.itemId]

  // TODO: Add a defensive check in case item is undefined (e.g. keg.itemId is invalid) to prevent runtime crash when accessing item.id
  let imageSrc = items[item.id as keyof typeof items]

  const recipeName = getKegDisplayName(item)

  const handleSellClick = () => {
    handleSellKegClick(keg)
  }

  const handleThrowAwayClick = () => {
    handleThrowAwayKegClick(keg)
  }

  const canBeSold = keg.daysUntilMature <= 0
  const kegValue =
    getKegValue(keg) * getSalePriceMultiplier(completedAchievements)

  if (wineService.isWineRecipe(item)) {
    imageSrc = wines[item.variety]
  }

  const spoilageRate = getKegSpoilageRate(keg)
  const spoilageRateDisplayValue = Number((spoilageRate * 100).toPrecision(2))

  return (
    <Card
      className="Keg"
      sx={{
        position: 'relative',
        '& .MuiCardHeader-avatar img': {
          width: '3em',
          filter: 'drop-shadow(1px 1px 2px rgba(255, 0, 131, 0.63))',
        },
      }}
    >
      <CardHeader
        title={recipeName}
        avatar={
          <img
            {...{
              src: imageSrc,
            }}
            alt={recipeName}
          />
        }
        subheader={
          <>
            {canBeSold ? (
              <>
                <p>Days since ready: {Math.abs(keg.daysUntilMature)}</p>
                <p>
                  Current value:{' '}
                  <AnimatedNumber
                    {...{ number: kegValue, formatter: moneyString }}
                  />
                </p>
                {cellarService.doesKegSpoil(keg) && (
                  <p>Potential for spoilage: {spoilageRateDisplayValue}%</p>
                )}
              </>
            ) : (
              <p>Days until ready: {keg.daysUntilMature}</p>
            )}
          </>
        }
      ></CardHeader>
      <CardActions>
        <Button
          {...{
            className: 'sell-keg',
            color: 'error',
            onClick: handleSellClick,
            variant: 'contained',
            disabled: !canBeSold,
          }}
        >
          Sell
        </Button>
        {!canBeSold ? (
          <Button
            {...{
              className: 'throw-away-keg',
              color: 'error',
              onClick: handleThrowAwayClick,
              variant: 'contained',
            }}
          >
            Throw away
          </Button>
        ) : null}
      </CardActions>
    </Card>
  )
}
